import { useState } from "react";
import { useStore } from "../lib/storeContext";
import { deleteEntityCascade } from "../lib/mutations";
import type { EntityType } from "../lib/types";

/**
 * Delete (tombstone) an entity and its descendants, propagating to the app + other devices.
 * Mirrors the iOS delete capability so the web is a full peer — and lets the user prune records
 * the app orphaned in the cloud via old hard-deletes (e.g. deleted scans that lingered).
 */
export function DeleteButton({
  type,
  id,
  what,
  onDeleted,
}: {
  type: EntityType;
  id: string;
  what: string;
  onDeleted?: () => void;
}) {
  const { store, touch } = useStore();
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  // Shared-project role gating (mirrors the server's enforcement matrix):
  // viewers delete nothing; editors delete everything EXCEPT the project itself.
  const role = store.roleFor(store.projectIdOf(type, id));
  if (role === "viewer") return null;
  if (type === "PROJECT" && role !== "owner") return null;

  async function go() {
    const extra =
      type === "PROJECT" || type === "ROOM" ? " and everything inside it" : "";
    if (!window.confirm(`Delete this ${what}${extra}? This removes it everywhere and can't be undone.`)) return;
    setBusy(true);
    setErr(null);
    try {
      await deleteEntityCascade(store, type, id);
      touch();
      onDeleted?.();
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Failed");
      touch();
    } finally {
      setBusy(false);
    }
  }

  return (
    <button
      className="rounded-full p-1.5 text-muted hover:bg-red-50 hover:text-red-600 disabled:opacity-50"
      title={`Delete ${what}`}
      onClick={go}
      disabled={busy}
    >
      {busy ? "…" : "🗑"}
      {err && <span className="ml-1 text-xs text-red-500">{err}</span>}
    </button>
  );
}
